import React from 'react';
import api from '../utils/api';
import useUserStore from '../store/userStore';
import useAiLogJobs from '../hooks/useAiLogJobs';
import { JobStatusCard, ReadyMealModal } from './AiLogJobCard';
import { shouldCloseOpenReadyModal } from './aiLogModalLogic';

// PHASE 3 — shared background-jobs section for Home + AI Log (identical behavior). Shows active/recent
// durable jobs (queued/processing/ready/failed). Surfaces the READY modal ONCE per newly-ready job:
// dismissed job_ids are tracked at MODULE scope (shared across Home <-> AI Log for the current app
// session) so a modal dismissed on one screen does NOT reopen when the user navigates to the other and a
// fresh section mounts. Dismissal is client-only (no backend state) and the READY card stays available.
// Multiple ready jobs are queued one-at-a-time (never lost/auto-opened beyond a single deliberate
// prompt). Renders nothing when the feature flag is OFF (403) -> legacy intact.
const DISMISSED_READY = new Set();   // module-lifetime: job_ids whose READY modal was closed this session

export function AiLogJobsSection({ title = 'AI meal analysis', compact = false }) {
  // PHASE 3 fix (C): only ever touch the protected /jobs endpoint when the soft `jobs_available` signal
  // is true. availability is non-raising (never 402), so a Free/ineligible user never trips the paywall
  // just by opening Home. Polling starts only once jobs_available flips true.
  // Availability belongs to the account it was checked for: true = allowed, false = denied, null = not
  // confirmed (still pending OR a transient failure). Another account's answer is never used.
  const ownerId = useUserStore((s) => (s.user && s.user.user_id) || null);
  const [access, setAccess] = React.useState({ owner: ownerId, allowed: null });
  React.useEffect(() => {
    let alive = true;
    api.get('/ai-log/availability')
      .then((r) => { if (alive) setAccess({ owner: ownerId, allowed: !!(r.data && r.data.jobs_available) }); })
      .catch(() => { if (alive) setAccess({ owner: ownerId, allowed: null }); });
    return () => { alive = false; };
  }, [ownerId]);
  const jobsAllowed = access.owner === ownerId ? access.allowed : null;

  // The protected /jobs list is still fetched ONLY after availability allows it for this account.
  const { jobs, serverJobs, acceptedIds, disabled, retry, discard } = useAiLogJobs(jobsAllowed === true);
  const [modalJob, setModalJob] = React.useState(null);

  const active = jobs.filter((j) => ['queued', 'processing', 'ready', 'failed'].includes(j.status));
  // Until availability is confirmed show ONLY jobs this account just had accepted; a denial shows nothing.
  const visible = jobsAllowed === true ? active
    : (jobsAllowed === false ? [] : active.filter((j) => acceptedIds.has(j.job_id)));

  React.useEffect(() => {
    // If a modal is already open, keep it one-at-a-time BUT auto-close it the moment its job leaves
    // READY (committed/discarded/retried) or disappears — otherwise a stale "ready to review" modal
    // stays covering Home after the user logs the meal from Review.
    if (modalJob) {
      if (shouldCloseOpenReadyModal(modalJob, serverJobs)) setModalJob(null);
      return;
    }
    const readyIds = new Set(serverJobs.filter((j) => j.status === 'ready').map((j) => j.job_id));
    // Forget dismissals for jobs that are no longer READY (committed/discarded/retried) so a genuinely
    // new READY transition can notify again. Guard on a loaded list so a transient empty poll never wipes
    // a still-valid dismissal (which would wrongly reopen the same modal on a screen change).
    if (serverJobs.length) {
      DISMISSED_READY.forEach((id) => { if (!readyIds.has(id)) DISMISSED_READY.delete(id); });
    }
    const next = serverJobs.find((j) => j.status === 'ready' && !DISMISSED_READY.has(j.job_id));
    if (next) setModalJob(next);
  }, [serverJobs, modalJob]);

  const closeModal = () => {                              // X: dismiss ONLY (no discard/commit/nav/status)
    if (modalJob) DISMISSED_READY.add(modalJob.job_id);
    setModalJob(null);
  };

  if (disabled || visible.length === 0) return null;

  return (
    <section className={compact ? 'space-y-2' : 'space-y-3'} data-testid="ai-log-jobs-section">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <div className="space-y-2">
        {visible.map((job) => (
          <JobStatusCard key={job.job_id} job={job} onRetry={retry} onDiscard={discard} />
        ))}
      </div>
      <ReadyMealModal job={jobsAllowed === true ? modalJob : null} onClose={closeModal} />
    </section>
  );
}

export default AiLogJobsSection;
